import { ArchiveAPI } from "api/archive";
import { FilesAPI } from "api/files";
import { downloadAndSaveFile } from "common/downloadFile";
import { getTabToken } from "common/MetaActions";
import config from "config/config";
import {
  setEmpty,
  setEuovrLast,
  setFoldersParents,
  setOpenFolders,
  sortEuFolder
} from "helpers/ArchiveHelper";
import _ from "lodash";
import { initialArchiveState } from "models/archive/archive";
import { ArchiveItem } from "models/ArchiveItem";
import { DeliveryFile } from "models/DeliveryFile";
import { DocumentUploadFile } from "models/DocumentUploadFile";
import { RootState } from "store";
import {
  getDocumentArchiveOptions,
  NODE_TYPE_ROOT
} from "views/documents/state/documentArchiveOptions";
import { StateCreator } from "zustand";

export interface ArchiveSlice {
  isMovingFile: boolean;
  isUploading: boolean;
  isLoadingFolder: boolean;
  isLoadingArchiveRoot: boolean;
  archiveRoot: ArchiveItem[];
  selectedFolderId: string;
  selectedFolderFiles: ArchiveItem[];
  requestCounter: number;

  incrementRequestCounter: () => void;
  getFolderFiles: (archiveItems: ArchiveItem[]) => void;
  setLoadingArchiveFolder: (isLoading: boolean) => void;
  setLoadingArchiveFolders: (isLoading: boolean) => void;
  resetArchive: () => void;
  getArchiveFolders: (archiveRoot: ArchiveItem[]) => void;
  failedToGetArchiveFolders: () => void;
  setSelectedArchiveNodeId: (nodeId: string) => void;
  toggleArchiveTreeNodeId: (id: string) => void;
  filesUploading: () => void;
  filesUploaded: () => void;
  fileMoving: () => void;
  fileMoved: (fileId: string) => void;
  updateFileName: (name: string, id: string) => void;
  getArchiveFolderFiles: (customerId: string, folderId: string) => void;
  updateFileWithName: (
    name: string,
    fileId: string,
    customerId: string
  ) => void;
  restoreFile: (itemId: string, customerId: string) => void;
  markAsDeleted: (itemId: string, customerId: string) => void;
  downloadFile: (file: ArchiveItem | DeliveryFile) => void;
  downloadArchiveFile: (file: ArchiveItem | DeliveryFile) => void;
  fetchArchiveFolders: (customerId: string, showWarning?: boolean) => void;
  moveInArchive: (
    item: ArchiveItem | DeliveryFile,
    customerId: string,
    path: string,
    name: string
  ) => void;
  sendFileToArchive: (
    items: DocumentUploadFile[],
    customerId: string,
    path: string,
    setFailedWrapper: (isFailed: boolean, message: string) => boolean
  ) => Promise<any>;
}

export const createArchiveSlice: StateCreator<
  RootState,
  [["zustand/devtools", never]],
  [],
  ArchiveSlice
> = (set, get) => ({
  ...initialArchiveState,
  requestCounter: 0,

  incrementRequestCounter: () =>
    set(({ archive }) => ({
      archive: {
        ...archive,
        requestCounter: get().archive.requestCounter + 1
      }
    })),

  getFolderFiles: (archiveItems) =>
    set(
      ({ archive }) => ({
        archive: {
          ...archive,
          selectedFolderFiles: archiveItems
        }
      }),
      false,
      "archive/GET_FOLDER_FILES"
    ),

  setLoadingArchiveFolder: (isLoading) =>
    set(
      ({ archive }) => ({
        archive: {
          ...archive,
          isLoadingFolder: isLoading
        }
      }),
      false,
      "archive/SET_LOADING_ARCHIVE_FOLDER"
    ),

  setLoadingArchiveFolders: (isLoading) =>
    set(
      ({ archive }) => ({
        archive: {
          ...archive,
          isLoadingArchiveRoot: isLoading
        }
      }),
      false,
      "archive/SET_LOADING_ARCHIVE_FOLDERS"
    ),

  resetArchive: () =>
    set(
      ({ archive }) => ({
        archive: {
          ...archive,
          isLoadingArchiveRoot: true,
          archiveRoot: [],
          selectedFolderFiles: [],
          selectedFolderId: NODE_TYPE_ROOT
        }
      }),
      false,
      "archive/RESET_ARCHIVE"
    ),

  getArchiveFolders: (archiveRoot) => {
    const archiveClone = _.cloneDeep(get().archive);
    archiveClone.archiveRoot = archiveRoot;
    archiveClone.selectedFolderId =
      archiveClone.selectedFolderId || NODE_TYPE_ROOT;

    setFoldersParents(
      getDocumentArchiveOptions(archiveRoot),
      archiveClone.archiveRoot,
      NODE_TYPE_ROOT
    );

    setEmpty(
      archiveClone.archiveRoot,
      archiveClone.archiveRoot.filter((a) => a.empty === false)
    );

    archiveClone.archiveRoot = sortEuFolder(archiveClone.archiveRoot);
    archiveClone.archiveRoot = setEuovrLast(archiveClone.archiveRoot);

    const history = _.remove(archiveClone.archiveRoot, (ar) => {
      return ar.id === "history";
    });

    if (history.length === 1) {
      archiveClone.archiveRoot.push(history[0]);
    }

    setOpenFolders(archiveClone.archiveRoot, archiveClone.selectedFolderId);
    archiveClone.isLoadingArchiveRoot = false;

    set(
      ({ archive }) => ({
        archive: {
          ...archive,
          ...archiveClone
        }
      }),
      false,
      "archive/GET_ARCHIVE_FOLDERS"
    );
  },

  failedToGetArchiveFolders: () =>
    set(
      ({ archive }) => ({
        archive: {
          ...archive,
          isLoadingArchiveRoot: false
        }
      }),
      false,
      "archive/FAILED_TO_GET_ARCHIVE_FOLDERS"
    ),

  setSelectedArchiveNodeId: (nodeId) =>
    set(
      ({ archive }) => ({
        archive: {
          ...archive,
          selectedFolderId: nodeId
        }
      }),
      false,
      "archive/SET_SELECTED_ARCHIVE_NODE_ID"
    ),

  toggleArchiveTreeNodeId: (id) => {
    const archiveRoot = [...get().archive.archiveRoot]; // clone to trigger state change
    setOpenFolders(archiveRoot, id);

    set(
      ({ archive }) => ({
        archive: {
          ...archive,
          archiveRoot,
          selectedFolderFiles: []
        }
      }),
      false,
      "archive/TOGGLE_ARCHIVE_TREE_NODE_ID"
    );
  },

  filesUploading: () =>
    set(
      ({ archive }) => ({
        archive: {
          ...archive,
          isUploading: true
        }
      }),
      false,
      "archive/FILES_UPLOADING"
    ),

  filesUploaded: () =>
    set(
      ({ archive }) => ({
        archive: {
          ...archive,
          isUploading: false
        }
      }),
      false,
      "archive/FILES_UPLOADED"
    ),

  fileMoving: () =>
    set(
      ({ archive }) => ({
        archive: {
          ...archive,
          isMovingFile: true
        }
      }),
      false,
      "archive/FILE_MOVING"
    ),

  fileMoved: (fileId) =>
    set(
      ({ archive }) => ({
        archive: {
          ...archive,
          isMovingFile: false,
          selectedFolderFiles: _.remove(
            get().archive.selectedFolderFiles,
            (file) => {
              return file.id !== fileId;
            }
          )
        }
      }),
      false,
      "archive/FILE_MOVED"
    ),

  updateFileName: (name, id) => {
    const selectedFolderFiles = [...get().archive.selectedFolderFiles];
    const file = selectedFolderFiles.find((f) => f.id === id);

    if (!file) {
      throw new Error("Filen finns inte tillgänglig i den valda vyn.");
    }

    file.name = name;
    set(
      ({ archive }) => ({
        archive: {
          ...archive,
          selectedFolderFiles
        }
      }),
      false,
      "archive/UPDATE_FILE_NAME"
    );
  },

  getArchiveFolderFiles: async (customerId, folderId) => {
    get().archive.incrementRequestCounter();
    const requestId = get().archive.requestCounter;

    get().archive.setLoadingArchiveFolder(true);

    try {
      const response = await ArchiveAPI.getArchiveFolderFiles(
        customerId,
        folderId
      );

      // Ignore stale request
      if (requestId !== get().archive.requestCounter) {
        return;
      }

      get().archive.getFolderFiles(response.archiveItems);
    } catch (err) {
      if (requestId !== get().archive.requestCounter) {
        return;
      }
      let statusCode: number | undefined;

      if (err && typeof err === "object" && "status" in err) {
        statusCode = (err as { status: number }).status;
      }
      if (statusCode === 404) {
        console.error(err);
      } else {
        get().error.appendErrorNotification(
          "Dokumentarkivet kunde inte öppnas.",
          err
        );
      }
      get().archive.failedToGetArchiveFolders();
    } finally {
      if (requestId === get().archive.requestCounter) {
        get().archive.setLoadingArchiveFolder(false);
      }
    }
  },

  updateFileWithName: async (name, fileId, customerId) => {
    const { archive, error } = get();
    archive.updateFileName(name, fileId);

    set({}, false, "archive/UPDATE_FILE_WITH_NAME");

    try {
      await FilesAPI.updateFileWithName(name, fileId, customerId);
    } catch (err) {
      error.appendErrorNotification(
        "Ett fel uppstod vid uppdatering av filnamnet.",
        err
      );
    }
  },

  restoreFile: async (itemId, customerId) => {
    const { archive, error } = get();
    archive.fileMoving();
    set({}, false, "archive/RESTORE_FILE");
    try {
      await ArchiveAPI.restoreFile(itemId, customerId);
      archive.fileMoved(itemId);
    } catch (err) {
      error.appendErrorNotification(
        "Återställning av filen misslyckades.",
        err
      );
    }
  },

  markAsDeleted: async (itemId, customerId) => {
    const { archive, error } = get();
    archive.fileMoving();
    set({}, false, "archive/MARK_AS_DELETED");
    try {
      await ArchiveAPI.markAsDeleted(itemId, customerId);
      archive.fileMoved(itemId);
    } catch (err) {
      error.appendErrorNotification("Borttagning av filen misslyckades.", err);
    }
  },

  downloadFile: async (file) => {
    set({}, false, "archive/DOWNLOAD_FILE");

    const headers = new Headers();
    headers.append("Authorization", `Bearer ${getTabToken()}`);
    const { workingCustomerId } = get().global;

    try {
      await downloadAndSaveFile(
        config.api.file.download(workingCustomerId, file.id),
        file.name,
        headers
      );
    } catch (err) {
      const { appendErrorNotification } = get().error;
      appendErrorNotification("Det gick inte att ladda ner filen.", err);
    }
  },

  downloadArchiveFile: async (file) => {
    set({}, false, "archive/DOWNLOAD_ARCHIVE_FILE");

    const headers = new Headers();
    headers.append("Authorization", `Bearer ${getTabToken()}`);
    const { workingCustomerId } = get().global;

    try {
      await downloadAndSaveFile(
        config.api.archive.download(workingCustomerId, file.id),
        file.name,
        headers
      );
    } catch (err) {
      const { appendErrorNotification } = get().error;
      appendErrorNotification("Det gick inte att ladda ner filen.", err);
    }
  },

  fetchArchiveFolders: async (customerId, showWarning = false) => {
    set({}, false, "archive/FETCH_ARCHIVE_FOLDERS");

    const { archive, error } = get();
    archive.setLoadingArchiveFolders(true);

    try {
      const response = await ArchiveAPI.fetchArchiveFolders(customerId);
      archive.getArchiveFolders(response.body.archiveItems);
    } catch (err) {
      let statusCode: number | undefined;

      if (err && typeof err === "object" && "status" in err) {
        statusCode = (err as { status: number }).status;
      }
      if (statusCode === 404) {
        if (showWarning) {
          error.handleCustomWarningMessage(
            statusCode,
            "Dokumentarkivet innehåller inga mappar."
          );
        }
      } else {
        error.appendErrorNotification(
          "Hämtning av dokumentarkivet misslyckades.",
          err
        );
      }
      archive.failedToGetArchiveFolders();
    }
  },

  moveInArchive: async (item, customerId, path, name) => {
    set({}, false, "archive/MOVE_IN_ARCHIVE");

    const { archive, error } = get();
    archive.fileMoving();

    try {
      await ArchiveAPI.moveInArchive(item, customerId, path, name);
      archive.fileMoved(item.id);
    } catch (err) {
      error.appendErrorNotification(
        "Flyttning av filen i dokumentarkivet misslyckades.",
        err
      );
    }
  },

  sendFileToArchive: async (items, customerId, path, setFailedWrapper) => {
    set({}, false, "archive/SEND_FILE_TO_ARCHIVE");
    const { archive, error } = get();
    archive.filesUploading();

    const uploadFilesAndConfirm = async (urls) => {
      const itemsAndUrls = urls.map(function (url, i) {
        return { preparedUploadResponse: url, item: items[i] };
      });

      return await Promise.all(
        itemsAndUrls.map(async (itemAndUrl) => {
          await ArchiveAPI.uploadToS3(
            itemAndUrl.preparedUploadResponse,
            itemAndUrl.item
          );
          await ArchiveAPI.restoreFile(
            itemAndUrl.preparedUploadResponse.fileId,
            customerId
          );
          return {
            id: itemAndUrl.preparedUploadResponse.fileId,
            name: itemAndUrl.item.file.name,
            type: itemAndUrl.item.file.type
          };
        })
      );
    };

    try {
      if (items.length === 0) {
        return [];
      }
      const urlsResponse = await ArchiveAPI.getUploadUrls(
        items,
        customerId,
        path
      );
      return await uploadFilesAndConfirm(urlsResponse);
    } catch (response) {
      const err = response as any;
      try {
        const errors = err?.response?.body?.errors;
        Object.keys(errors).forEach((key) => {
          setFailedWrapper(true, errors[key][0]);
        });
        return;
      } catch {
        get().error.appendErrorNotification(
          "Filöverföringen misslyckades.",
          "Misslyckades med att tolka felresponsen"
        );
      }
      error.appendErrorNotification(
        "Överföring av filen till dokumentarkivet misslyckades.",
        err
      );
      return Promise.reject();
    } finally {
      const { customers } = get();
      const { workingCustomer: customer } = customers;
      archive.filesUploaded();
      archive.setLoadingArchiveFolder(false);

      archive.getArchiveFolderFiles(
        customer.id || "",
        archive.selectedFolderId
      );
    }
  }
});
